import PaymentMethodsComponent from "components/paymentMethod/PaymentMethodsComponent";
import { valuesProps } from "types/paymentMethods";
import { CashAppIcon } from "icons/dashboard/cashAppIcon";
import { BankIconSm } from "icons/payments/BankIconSm";
import { CardIcon } from "icons/payments/Card";
import { PaypalIcon } from "icons/dashboard/paypal";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import expiryDateValidation, {
  checkBankManualFormOrPlaid,
  checkNewPaymentDetails,
  formatExpirationDate,
  getBankName,
  getCardType,
  getOperatingSystem,
  getPaymentMethod,
  validateCard,
} from "utils/HelperFunctions";
import Button from "components/form/Button";
import {
  chargeCustomerOnSubscribe,
  storePlanDetails,
  updatePlan,
  updateSubscriptionPaymentMethod,
} from "store/slices/subscriptionSlice";
import {
  convFeeGet,
  createPaypalToken,
  duplicatePaymentGet,
  oneTimeMakePayment,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  setIsPaymentProcessing,
  storePayPalOrder,
  verifyWalletPay,
} from "store/slices/paymentsSlice";
import { addNotification, useUserData } from "store/slices/authSlice";
import AmazonPay from "icons/dashboard/AmazonPay";
import { decode } from "utils/HelperFunctions";
import { addCard, usePaymentMethodData } from "store/slices/paymentMethodSlice";
import { initialValues } from "utils/countryAddressList";
import { formatCardExpiry } from "utils/DateFormat";
import {
  FormRef,
  PaymentGateway,
  PaymentMethodList,
  SettingsValue,
} from "types/payments";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { RootState } from "store/store";
import { VerifyStripeCardPaymentParams } from "types/agreement";

interface PaymentSubscribePlanProps {
  id?: any;
  currentPlan: any;
  amount: any;
  setPaymentSuccess: any;
  setCurrentStep: any;
  newProducts?: any[];
  setScreen?: any;
  isManualForm: boolean;
  setIsManualForm: (val: boolean) => void;
  isUpdateSubscriptionPaymentMethod?: boolean;
  isDowngradePlan?: boolean;
  setShowPopup?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBTNLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  onPaymentMethodChanged?: () => Promise<void>;
}

const PaymentSubscribePlan = forwardRef(
  (props: PaymentSubscribePlanProps, ref) => {
    const {
      currentPlan,
      amount,
      setPaymentSuccess,
      setCurrentStep,
      id,
      newProducts = [],
      setScreen,
      isManualForm,
      setIsManualForm,
      isUpdateSubscriptionPaymentMethod = false,
      isDowngradePlan = false,
      setShowPopup = () => {},
      setIsBTNLoading = () => {},
      onPaymentMethodChanged = async () => {},
    } = props;
    const paymentMethodListData = usePaymentMethodData();

    const dispatch = useAppDispatch();
    const userData = useUserData();
    const accountLeadData = useLeadData();
    const cashAppRef = useRef<FormRef>(null);
    const bankRef = useRef<FormRef>(null);
    const cardRef = useRef<FormRef>(null);
    const amazonRef = useRef<FormRef>(null);
    const { conFee } = useAppSelector((state: RootState) => state.payment);

    const [pMethodSelected, setPMethodSelected] = useState<{
      id: number;
      type: string;
    }>({ id: 0, type: "old_method" });

    const [limits, setLimits] = useState(3);

    const {
      paymentPermission,
      isFeeLoading,
      payPalOrder,
      checkedDuplicatePayment,
    } = useAppSelector((state: RootState) => state.payment);

    const showFinalDisclosure = useShowFinalDisclosure();
    const [selectedMethod, setSelectedMethod] = useState<
      "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
    >("Card");

    const portalSettings = usePortalSettingsData();
    const [isAddNewCardMakePayment, setIsAddNewCardMakePayment] =
      useState<boolean>(true);
    const [values, setValues] = useState<valuesProps>(initialValues);
    const [email, setEmail] = useState<string>("");
    const [cardNumber, setcardNumber] = useState<string>("");
    const [iconType, setIconType] = useState<string>("");
    const [selectedState, setSelectedState] = useState<any>(undefined);
    const [isDateErr, setIsDateErr] = useState<boolean>(false);
    const [bankName, setBankName] = useState<any>("");
    const [bank, setBank] = useState<any>("");
    const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
    const [isRingLoading, setIsRingLoading] = useState<boolean>(false);
    const [numberNotmatchedError, setnumberNotmatchedError] =
      useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isErr, setIsErr] = useState(-1);
    const [cardErrMsg, setCardErrMsg] = useState("");
    const [showButtons, setShowButtons] = useState<boolean>(false);
    const defaultPaymentMethodList: PaymentMethodList[] = [
      {
        name: "Card",
        icon: <CardIcon />,
      },
      {
        name: "Bank",
        icon: <BankIconSm />,
      },
    ];
    const [paymentMethodList, setPaymentMethodList] = useState<
      PaymentMethodList[]
    >(defaultPaymentMethodList);

    useEffect(() => {
      if (!cardNumber) {
        setIconType("");
        setValues({ ...values, card_type: "" });
        return;
      } else {
        setIconType(getCardType(cardNumber));
        setValues({ ...values, card_type: getCardType(cardNumber) });
      }
    }, [cardNumber]);

    useEffect(() => {
      if (paymentPermission) {
        let methodList = [];
        if (paymentPermission.cc_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Card",
            icon: <CardIcon />,
          });
        }

        if (paymentPermission.ach_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Bank",
            icon: <BankIconSm />,
          });
        }

        if (paymentPermission.pp_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "PayPal",
            icon: <PaypalIcon />,
          });
        }

        if (paymentPermission.capp_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Cash App Pay",
            icon: <CashAppIcon />,
          });
        }

        if (paymentPermission.amzp_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Amazon Pay",
            icon: <AmazonPay />,
          });
        }
        if (paymentPermission.cc_ext_availability === SettingsValue.NO) {
          setSelectedMethod(methodList?.[0]?.name);
        }

        setPaymentMethodList(methodList);
      } else {
        setPaymentMethodList(defaultPaymentMethodList);
      }
    }, [paymentPermission]);

    useEffect(() => {
      let response = checkNewPaymentDetails({
        values,
        cc_billing_address: portalSettings?.cc_billing_address,
        cc_billing_addr_mandatory: portalSettings?.cc_billing_addr_mandatory,
        selectedMethod,
        email,
        isErr,
        errMsg,
      });
      setIsDisabled(response);
    }, [selectedMethod, values, email, isErr, errMsg]);

    const findBankByRoutingNumber = async (routingNumber: string) => {
      setIsRingLoading(true);
      try {
        let bankObj = await getBankName(routingNumber);
        if (bankObj?.type === "success") {
          setBankName(bankObj?.bank_name);
          setBank(bankObj?.bank_name);
          setErrMsg(undefined);
        } else {
          setErrMsg("Invalid routing number");
          setIsRingLoading(false);
        }
      } catch (error) {
        console.log("error =====", error);
        setErrMsg("Invalid routing number");
        setIsRingLoading(false);
      } finally {
        setIsRingLoading(false);
      }
    };

    const handleChangeCard = (e: any, name: string) => {
      let value = { ...values };
      if (name === "x_card_num") {
        if (iconType === "amex") {
          if (e.target.value.length < 18) {
            const cardNumber = e.target.value.replace(/\s/g, "");
            const formattedCardNumber = cardNumber
              .replace(/\D/g, "")
              .replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
            setcardNumber(formattedCardNumber);

            value = { ...values, [name]: e.target.value };
          }
        } else {
          if (e.target.value.length < 20) {
            const cardNumber = e.target.value.replace(/\s/g, "");
            const formattedCardNumber = cardNumber
              .replace(/\D/g, "")
              .replace(/(\d{4})(?=\d)/g, "$1 ");
            setcardNumber(formattedCardNumber);

            value = { ...values, [name]: e.target.value };
          }
        }
      } else if (name === "x_card_code") {
        if (iconType === "amex") {
          if (e.target.value.length < 5) {
            value = { ...values, [name]: e.target.value };
          }
        } else {
          if (e.target.value.length < 4) {
            value = { ...values, [name]: e.target.value };
          }
        }
      } else if (name === "x_exp_date") {
        const inputValue = e.target.value;
        setIsDateErr(false);
        if (inputValue?.length < 6) {
          const isValidDate: any = expiryDateValidation(inputValue);
          const formattedDate = formatExpirationDate(inputValue);
          value = { ...value, [name]: formattedDate };
          !isValidDate?.isValid &&
            inputValue?.length === 5 &&
            setIsDateErr(true);
        }
      } else if (name === "x_country") {
        value = { ...value, [name]: e?.value };
      } else if (name === "x_state") {
        value = { ...value, [name]: e?.name };
      } else {
        value = { ...value, [name]: e.target.value };
      }

      setValues(value);
    };

    const handleChangeBank = (e: any, name: string) => {
      if (name === "routing_number") {
        if (e?.target?.value?.length < 10) {
          setValues({ ...values, [name]: e.target.value });
          setBankName("");
          setBank("");
          setErrMsg(undefined);
          if (e?.target?.value?.length === 9) {
            findBankByRoutingNumber(e?.target?.value);
          }
        }
      } else if (name === "r_account_number") {
        setnumberNotmatchedError(false);
        setValues({ ...values, [name]: e?.target.value });
        if (values?.account_number?.length === e?.target?.value?.length) {
          setnumberNotmatchedError(values?.account_number !== e?.target?.value);
        }
      } else {
        setValues({ ...values, [name]: e?.target?.value });
      }
    };

    const createPayPalIntent = async () => {
      try {
        const payload = {
          is_recurring: "Y",
          phone_number: userData?.data?.accountData?.phone,
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          accountid: userData?.data?.accountData?.accountid,
          amount: amount,
          agent: userData?.data?.accountData?.lead_owner,
          lead_owner: userData?.data?.accountData?.lead_owner,
          lead_id: accountLeadData?.lead_id,
          clusterid: decode(id)?.split("_")?.[1],
          success_url: `${
            window.location.href
          }?method=paypal&amount=${amount}&subsDrawer=true&status=success&isRecurring=${true}&subscriptionId=${
            currentPlan?.id
          }&lead_id=${currentPlan?.lead_id}&clusterid=${
            currentPlan?.clusterid
          }&subsId=${currentPlan?.id}`,
          cancel_url: `${
            window.location.href
          }?method=paypal&amount=${amount}&subsDrawer=true&status=cancel&isRecurring=${true}&subscriptionId=${
            currentPlan?.id
          }&lead_id=${currentPlan?.lead_id}&clusterid=${
            currentPlan?.clusterid
          }&subsId=${currentPlan?.id}`,
          start_date: new Date(),
        };
        let res: any = await dispatch(createPaypalToken(payload));
        if (res?.payload?.body?.success) {
          await dispatch(
            storePayPalOrder({
              url: res?.payload?.body?.result?.url,
              orderId: res?.payload?.body?.result?.invoice_id,
              token: res?.payload?.body?.result?.token,
              currentPlan: currentPlan,
              amount: amount,
              id: id,
            })
          );

          let moveAhead = await handleCheckDisclosureAndDuplicate({
            method: "PP",
          });
          if (!moveAhead) {
            return res;
          } else {
            window.location.href = res?.payload?.body?.result?.url;
          }
        }
      } catch (error) {
        console.log("createPayPalIntent error =====", error);
      }
    };

    const onUpdatePlan = async (payload: any) => {
      try {
        let plan: any[] = [];
        newProducts.forEach((product: any) => {
          plan.push({
            qty: product?.quantity,
            product_id: product.id,
            price_id: product.pid,
          });
        });

        payload = {
          ...payload,
          plan,
          cc_4digit: payload?.card_last4 || "",
          cc_type: payload?.cc_type,
          cc_name: "",
          cc_cvv: payload?.cc_cvv || "",
          cc_expiry: payload?.cc_expiry || "",
          cc_token: payload?.cc_token || "",
          cc_token2: payload?.cc_token2 || "",
          bank_name: payload?.bank_name || "",
          subscription_slug: id,
          accounts_id: currentPlan?.accounts_id,
          accountid: userData?.data?.accountData?.accountid,
          lead_id: currentPlan?.lead_id,
          lead_owner: currentPlan?.lead_owner,
        };
        let res: any = await dispatch(updatePlan(payload));
        if (res?.payload?.body?.success) {
          setPaymentSuccess(true);
          setScreen(true);
          let subscription = decode(id || "")?.split("_");
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "subscription",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "modified",
              activity_type: "payment_activity",
              clusterid: subscription[1],
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
              payment_amount: payload.amount,
              subscription_id: subscription[0],
              comments: `Subscription for ${userData?.data?.accountData?.accountid} was updated`,
            })
          );
        } else {
          setPaymentSuccess(true);
          setScreen(false);
        }
      } catch (error) {
        console.log("onUpdatePlan error =====", error);
      } finally {
        setIsLoading(false);
      }
    };

    const getMethod = () => {
      if (pMethodSelected.type === "new_method") {
        return selectedMethod === "Card"
          ? "CC"
          : selectedMethod === "Bank"
            ? "ACH"
            : selectedMethod === "Cash App Pay"
              ? "CAPP"
              : selectedMethod === "Amazon Pay"
                ? "AMZP"
                : selectedMethod === "PayPal"
                  ? "PP"
                  : selectedMethod === "Wire"
                    ? "WT"
                    : undefined;
      } else if (pMethodSelected.type === "old_method") {
        return currentPlan?.payment_method;
      } else {
        const savedMethod = paymentMethodListData?.find(
          (x: any) => x.id === pMethodSelected.id
        );
        return savedMethod?.payment_method;
      }
    };
    useEffect(() => {
      const getConvFee = async () => {
        try {
          let params = {
            cluster_id: currentPlan?.clusterid,
            state:
              values?.x_state !== ""
                ? values?.x_state
                : userData?.data?.accountData?.state,
            x_method: getMethod(),
            phone_number: userData?.data?.accountData?.phone,
            x_amount: amount,
            lead_id: currentPlan?.lead_id,
            custom_conv: "on",
          };

          await dispatch(convFeeGet(params)).unwrap();
        } catch (error) {
          console.log("getConvFee error =====", error);
        }
      };
      if (amount > 0) {
        getConvFee();
      }
    }, [amount, selectedMethod, pMethodSelected, values?.x_state]);

    const makeUpdatePayment = async (payload: any) => {
      try {
        const res: any = await dispatch(oneTimeMakePayment(payload)).unwrap();
        return res;
      } catch (error) {
        console.log("error =====", error);
      }
    };

    // On a downgrade nothing is charged today, so /portal/update-plan ignores the
    // cc_* fields; the method swap must go through update-subscription-payment-method.
    const applyDowngradeMethodChange = async (cppId: any) => {
      try {
        let payload = {
          subscription_id: currentPlan?.id,
          cpp_id: `${cppId || ""}`,
        };
        let res: any = await dispatch(
          updateSubscriptionPaymentMethod(payload)
        ).unwrap();
        return !!res?.body?.success;
      } catch (error) {
        console.log("applyDowngradeMethodChange error =====", error);
        return false;
      }
    };

    const handleUpdatePayment = async (res: any) => {
      try {
        let value = {
          subscription_id: currentPlan?.id,
          lead_id: currentPlan?.lead_id || "",
          cluster_id: currentPlan?.clusterid,
          balance: userData?.data?.accountData?.balance,
          agent: currentPlan?.lead_owner,
          x_payment_method: selectedMethod === "Card" ? "CC" : "ACH",
          city: values?.x_city || userData?.data?.accountData?.city || "",
          address1:
            values?.x_address || userData?.data?.accountData?.address1 || "",
          address2:
            values?.address2 || userData?.data?.accountData?.address2 || "",
          zipcode:
            values?.x_zip || userData?.data?.accountData?.postal_code || "",
          state: values?.x_state || userData?.data?.accountData?.state || "",
          country:
            values?.x_country || userData?.data?.accountData?.country || "",
          first_name: userData?.data?.accountData?.first_name || "",
          last_name: userData?.data?.accountData?.last_name || "",
          accountid: userData?.data?.accountData?.accountid || "",
          amount: amount,
          email: userData?.data?.accountData?.email || "",
          amount_type: "amount",
          use_old_card: "1",
          description: "Online Payment",
          payment_note: "Subscription",
          customer_device: getOperatingSystem(),
          card_type: iconType || "",
          old_cardnumber: values?.x_card_num?.replaceAll(" ", "") || "",
          old_cvv: values?.x_card_code || "",
          old_expiry: values?.x_exp_date || "",
          old_4digit: values?.x_card_num?.replaceAll(" ", "").slice(-4) || "",
          old_type: iconType || "",
          old_zip: values?.x_zip || "",
          cvv: values?.x_card_code || "",
          cexpiry: values?.x_exp_date || "",
          ccardnumber: values?.x_card_num?.replaceAll(" ", "") || "",
          choldername: values?.choldername || "",
          aholdername: values?.choldername || "",
          routing_number: values?.routing_number || "",
          account_number: values?.account_number || "",
          bank_name: bank || "",
          bank_address: bank || "",
          lead_owner: currentPlan?.lead_owner,
          cc_token: res?.body?.data?.paymentMethod?.cc_token || "",
          cc_token2: res?.body?.data?.paymentMethod?.cc_token2 || "",
          stripe_token: res?.body?.data?.paymentMethod?.cc_token || "",
          cppid: res?.body?.data?.paymentMethod?.id || "",
          cfc: 0,
          isPayForProduct: true,
        };
        let pay = {
          subscription_slug: id,
          plan: [],
          email: currentPlan?.email || "",
          x_payment_method: selectedMethod === "Card" ? "CC" : "ACH",
          billing_city:
            values?.x_city || userData?.data?.accountData?.city || "",
          account_number: values?.account_number || "",
          routing_number: values?.routing_number || "",
          bank_address: bank || "",
          bank_name: bank || "",
          gateway_id: values?.gateway_id || "",
          cc_4digit:
            selectedMethod === "Card"
              ? values?.x_card_num?.slice(-4)
              : values?.account_number?.slice(-4) || "",
          cc_type: iconType || "",
          cc_name: values?.choldername || "",
          cc_cvv: values?.x_card_code || "",
          cc_expiry: values?.x_exp_date || "",
          cc_token: res?.body?.data?.paymentMethod?.cc_token || "",
          cc_token2: res?.body?.data?.paymentMethod?.cc_token2 || "",
          billing_state:
            values?.x_state || userData?.data?.accountData?.state || "",
          billing_zip:
            values?.x_zip || userData?.data?.accountData?.postal_code || "",
          billing_address2:
            values?.address2 || userData?.data?.accountData?.address2 || "",
          billing_address1:
            values?.x_address || userData?.data?.accountData?.address1 || "",
          x_country:
            values?.x_country || userData?.data?.accountData?.country || "",
          amount: amount,
          cppid: res?.body?.data?.paymentMethod?.id || "",
          accounts_id: currentPlan?.accounts_id,
          lead_id: currentPlan?.lead_id,
          lead_owner: currentPlan?.lead_owner,
        };
        if (isDowngradePlan) {
          const isMethodUpdated = await applyDowngradeMethodChange(
            res?.body?.data?.paymentMethod?.id
          );
          if (isMethodUpdated) {
            await onUpdatePlan(pay);
          } else {
            setScreen(false);
            setPaymentSuccess(true);
            setIsLoading(false);
          }
          return;
        }
        let result = await makeUpdatePayment(value);
        if (result && result?.body?.success) {
          await onUpdatePlan({
            ...pay,
            order_id: result?.body?.data?.invoice_id,
          });
        } else {
          setScreen(false);
          setPaymentSuccess(true);
        }
      } catch (error) {
        console.log("error ======", error);
        setScreen(false);
        setPaymentSuccess(true);
      }
    };

    const handleCreateNewCard = async (isUpdateMethod: boolean = false) => {
      try {
        let payload = {
          x_card_num: values?.x_card_num?.replaceAll(" ", ""),
          x_exp_date: values?.x_exp_date,
          x_card_code: values?.x_card_code,
          choldername: values?.choldername,
          x_payment_method: selectedMethod === "Card" ? "CC" : "ACH",
          x_city: values?.x_city || userData?.data?.accountData?.city || "",
          x_address:
            values?.x_address || userData?.data?.accountData?.address1 || "",
          address2:
            values?.address2 || userData?.data?.accountData?.address2 || "",
          x_zip:
            values?.x_zip || userData?.data?.accountData?.postal_code || "",
          x_state: values?.x_state || userData?.data?.accountData?.state || "",
          x_country:
            values?.x_country || userData?.data?.accountData?.country || "",
          routing_number: values?.routing_number,
          account_number: values?.account_number,
          bank_name: bank,
          swift_code: values?.swift_code,
          card_funding_type: values?.card_funding_type,
          default: "Y",
          default_profile: "N",
          cluster_id: currentPlan?.clusterid,
          accountid: userData?.data?.accountData?.accountid,
          x_first_name: userData?.data?.accountData?.first_name,
          x_last_name: userData?.data?.accountData?.last_name,
          x_phone: userData?.data?.accountData?.phone || "",
          x_email: userData?.data?.accountData?.email || "",
          lead_id: currentPlan?.lead_id || "",
          gateway_id:
            selectedMethod === "Card"
              ? paymentPermission?.cc_gateway
              : paymentPermission?.ach_gateway || "",
          gateway_name:
            selectedMethod === "Card"
              ? paymentPermission?.cc_gateway_name?.name
              : paymentPermission?.ach_gateway_name?.name || "",
          account_holder_name: values?.choldername ?? "",
          card_type: iconType,
          accounts_id: userData?.data?.accountData?.id,
        };
        let res: any = await dispatch(addCard(payload)).unwrap();

        if (res && res.body && res.body.success) {
          if (isUpdateMethod) {
            const { paymentMethod } = res.body.data;
            await updatePaymentMethod(paymentMethod?.id || "");
          } else {
            await handleUpdatePayment(res);
          }
        } else {
          setScreen(false);
          setPaymentSuccess(true);
        }
      } catch (error) {
        setScreen(false);
        setPaymentSuccess(true);
        console.log("handleCreateNewCard error =====", error);
      } finally {
        dispatch(onSetShowDisclosure(false));
        dispatch(onSetFinalDisclosureLoading(false));
      }
    };

    const createPayload = async () => {
      let payload: any = { slug: id };
      switch (currentPlan?.payment_method) {
        case "CC":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "CC",
            amount: amount,
            subscription_slug: id,
            payment_method_types: "card",
          };
          break;
        case "ACH":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "ACH",
            payment_method_types: "us_bank_account",
            amount: amount,
            subscription_slug: id,
          };
          break;
        case "CAPP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "CAPP",
            payment_method_types: "cashapp",
            amount: amount,
            subscription_slug: id,
          };
          break;
        case "AP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "AP",
            payment_method_types: "cashapp",
            amount: amount,
            subscription_slug: id,
          };
          break;
        case "GP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "GP",
            amount: amount,
            subscription_slug: id,
          };
          break;
        case "AMZP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "AMZP",
            payment_method_types: "amazon_pay",
            amount: amount,
            subscription_slug: id,
          };
          break;
        case "PP":
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: "PP",
            amount: amount,
            subscription_slug: id,
          };
          break;
        default:
          payload = {
            ...payload,
            cc_token: currentPlan?.cc_ref_number,
            payment_method: currentPlan?.payment_method,
            amount: amount,
            subscription_slug: id,
          };
      }
      return payload;
    };

    const handleOldMethod = async () => {
      setIsLoading(true);
      try {
        if (isUpdateSubscriptionPaymentMethod) {
          let cppid = currentPlan?.cpp_id;
          await updatePaymentMethod(cppid || "");
          setIsLoading(false);
        } else if (isDowngradePlan) {
          // Same method, nothing due today — no charge, just update the plan.
          await onUpdatePlan({
            subscription_slug: id,
            plan: [],
            email: currentPlan?.email || "",
            x_payment_method: currentPlan?.payment_method,
            billing_city: currentPlan?.billing_city || "",
            account_number: currentPlan?.account_number || "",
            routing_number: currentPlan?.routing_number || "",
            bank_address: currentPlan?.bank_address || "",
            bank_name: currentPlan?.bank_name || "",
            gateway_id: currentPlan?.gateway_id || "",
            cc_4digit: currentPlan?.cc_4digit || "",
            cc_type: currentPlan?.cc_type || "",
            cc_name: currentPlan?.cc_name || "",
            cc_cvv: currentPlan?.cc_cvv || "",
            cc_expiry: currentPlan?.cc_expiry || "",
            cc_token: currentPlan?.cc_ref_number || "",
            billing_state: currentPlan?.billing_state || "",
            billing_zip: currentPlan?.billing_zip || "",
            billing_address2: currentPlan?.billing_address2 || "",
            billing_address1: currentPlan?.billing_address1 || "",
            amount: amount,
            cppid: currentPlan?.cpp_id || "",
          });
        } else {
          let payload = await createPayload();
          payload = {
            ...payload,
            accounts_id: currentPlan?.accounts_id,
            lead_id: currentPlan?.lead_id,
            email: currentPlan?.email,
            accountid: userData?.data?.accountData?.accountid,
            clusterid: decode(id)?.split("_")?.[1],
            first_name: userData?.data?.accountData?.first_name,
            last_name: userData?.data?.accountData?.last_name,
            address1: currentPlan?.billing_address1,
            city: currentPlan?.billing_city,
            state: currentPlan?.billing_state,
            postal_code: currentPlan?.billing_zip,
            phone_number: currentPlan?.phone_number,
          };
          let res: any = await dispatch(
            chargeCustomerOnSubscribe(payload)
          ).unwrap();
          if (res?.body?.success) {
            let payload = {
              subscription_slug: id,
              plan: [],
              email: currentPlan?.email || "",
              x_payment_method: currentPlan?.payment_method,
              billing_city: currentPlan?.billing_city || "",
              account_number: currentPlan?.account_number || "",
              routing_number: currentPlan?.routing_number || "",
              bank_address: currentPlan?.bank_address || "",
              bank_name: currentPlan?.bank_name || "",
              gateway_id: currentPlan?.gateway_id || "",
              cc_4digit: currentPlan?.cc_4digit || "",
              cc_type: currentPlan?.cc_type || "",
              cc_name: currentPlan?.cc_name || "",
              cc_cvv: currentPlan?.cc_cvv || "",
              cc_expiry: currentPlan?.cc_expiry || "",
              cc_token: currentPlan?.cc_ref_number || "",
              billing_state: currentPlan?.billing_state || "",
              billing_zip: currentPlan?.billing_zip || "",
              billing_address2: currentPlan?.billing_address2 || "",
              billing_address1: currentPlan?.billing_address1 || "",
              amount: amount,
              cppid: currentPlan?.cpp_id || "",
              order_id: res?.body?.data?.invoice_id,
            };
            await onUpdatePlan(payload);
          } else {
            setPaymentSuccess(true);
            setScreen(false);
            setIsLoading(false);
          }
        }
      } catch (error) {
        setPaymentSuccess(true);
        setScreen(false);
        setIsLoading(false);
        console.log("error =====", error);
      } finally {
        dispatch(onSetShowDisclosure(false));
        dispatch(onSetFinalDisclosureLoading(false));
      }
    };

    const handleDefaultMethod = async () => {
      try {
        if (isUpdateSubscriptionPaymentMethod) {
          let cppid = `${pMethodSelected?.id}`;
          await updatePaymentMethod(cppid || "");
        } else {
          const old_Method = paymentMethodListData?.find(
            (method: any) => method.id === pMethodSelected?.id
          );
          if (isDowngradePlan) {
            const isMethodUpdated = await applyDowngradeMethodChange(
              old_Method?.id
            );
            if (isMethodUpdated) {
              await onUpdatePlan({
                subscription_slug: id,
                plan: [],
                email: currentPlan?.email || "",
                x_payment_method: old_Method?.payment_method,
                billing_city: old_Method?.billing_city || "",
                account_number: old_Method?.account_number || "",
                routing_number: old_Method?.routing_number || "",
                bank_address: old_Method?.bank_address || "",
                bank_name: old_Method?.bank_name || "",
                gateway_id: old_Method?.gateway_id || "",
                cc_4digit: old_Method?.cc_4digit || "",
                cc_type: old_Method?.cc_type || "",
                cc_name: old_Method?.cc_name || "",
                cc_cvv: old_Method?.cc_cvv || "",
                cc_expiry: old_Method?.cc_expiry || "",
                cc_token: old_Method?.cc_token || "",
                cc_token2: old_Method?.cc_token2 || "",
                billing_state: old_Method?.billing_state || "",
                billing_zip: old_Method?.billing_zip || "",
                billing_address2: old_Method?.billing_address2 || "",
                billing_address1: old_Method?.billing_address1 || "",
                amount: amount,
                cppid: old_Method?.id || "",
              });
            } else {
              setScreen(false);
              setPaymentSuccess(true);
              setIsLoading(false);
            }
            return;
          }
          let payload = {
            subscription_id: currentPlan?.id,
            lead_id: currentPlan?.lead_id || "",
            cluster_id: currentPlan?.clusterid,
            balance: userData?.data?.accountData?.balance,
            agent: currentPlan?.lead_owner,
            x_payment_method: old_Method?.payment_method,
            city: old_Method?.billing_city || "",
            address1: old_Method?.billing_address || "",
            address2: old_Method?.billing_address2 || "",
            zipcode: old_Method?.billing_zip || "",
            state: old_Method?.billing_state || "",
            country: old_Method?.billing_country || "",
            first_name: userData?.data?.accountData?.first_name || "",
            last_name: userData?.data?.accountData?.last_name || "",
            accountid: userData?.data?.accountData?.accountid || "",
            amount: amount,
            email: userData?.data?.accountData?.email || "",
            amount_type: "amount",
            use_old_card: "1",
            description: "Online Payment",
            payment_note: "Subscription",
            customer_device: getOperatingSystem(),
            card_type: old_Method?.cc_type || "",
            old_cardnumber: old_Method?.cc_4digit || "",
            old_cvv: old_Method?.cc_cvv || "",
            old_expiry: old_Method?.cc_expiry || "",
            old_4digit: old_Method?.cc_4digit || "",
            old_type: old_Method?.cc_type || "",
            old_zip: old_Method?.billing_zip || "",
            cvv: old_Method?.cc_cvv || "",
            cexpiry: old_Method?.cc_expiry || "",
            ccardnumber: old_Method?.cc_4digit || "",
            choldername: old_Method?.account_name || "",
            aholdername: old_Method?.account_name || "",
            routing_number: old_Method?.routing_number || "",
            account_number: old_Method?.account_number || "",
            bank_name: old_Method?.bank_name || "",
            bank_address: old_Method?.bank_address || "",
            cc_token: old_Method?.cc_token || "",
            cc_token2: old_Method?.cc_token2 || "",
            stripe_token: old_Method?.cc_token || "",
            lead_owner: currentPlan?.lead_owner,
            cfc: 0,
            isPayForProduct: true,
          };
          let res = await makeUpdatePayment(payload);
          if (res?.body?.success) {
            let values = {
              subscription_slug: id,
              plan: [],
              email: currentPlan?.email || "",
              x_payment_method: old_Method?.payment_method,
              billing_city: old_Method?.billing_city || "",
              account_number: old_Method?.account_number || "",
              routing_number: old_Method?.routing_number || "",
              bank_address: old_Method?.bank_address || "",
              bank_name: old_Method?.bank_name || "",
              gateway_id: old_Method?.gateway_id || "",
              cc_4digit: old_Method?.cc_4digit || "",
              cc_type: old_Method?.cc_type || "",
              cc_name: old_Method?.cc_name || "",
              cc_cvv: old_Method?.cc_cvv || "",
              cc_expiry: old_Method?.cc_expiry || "",
              cc_token: old_Method?.cc_token || "",
              billing_state: old_Method?.billing_state || "",
              billing_zip: old_Method?.billing_zip || "",
              billing_address2: old_Method?.billing_address2 || "",
              billing_address1: old_Method?.billing_address1 || "",
              amount: amount,
              cppid: old_Method?.id || "",
              order_id: res?.body?.data?.invoice_id,
            };
            await onUpdatePlan(values);
          } else {
            setScreen(false);
            setPaymentSuccess(true);
          }
        }
      } catch (error) {
        setScreen(false);
        setPaymentSuccess(true);
        console.log("handleDefaultMethod error =====", error);
      } finally {
        dispatch(onSetShowDisclosure(false));
        dispatch(onSetFinalDisclosureLoading(false));
      }
    };

    const checkPastAmount = async (method?: string) => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id,
          payment_method: method,
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async ({
      method,
    }: {
      method?: string;
    }) => {
      try {
        let moveAhead = !checkedDuplicatePayment
          ? await checkPastAmount(method)
          : true;
        if (!moveAhead) {
          dispatch(onSetFinalDisclosureLoading(false));
          setIsLoading(false);
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(resetDuplicateOrder());
            dispatch(onSetShowDisclosure(true));
            setIsLoading(false);
            return false;
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleMakePayment = async () => {
      setIsLoading(true);

      if (!isUpdateSubscriptionPaymentMethod) {
        let moveAhead = await handleCheckDisclosureAndDuplicate({
          method: getMethod(),
        });
        if (!moveAhead) {
          return;
        }
      }

      try {
        if (pMethodSelected.type === "old_method") {
          await handleOldMethod();
        } else if (pMethodSelected.type === "saved_method") {
          await handleDefaultMethod();
        } else {
          await handleCreateNewCard();
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    useEffect(() => {
      if (
        (selectedMethod === "Card" &&
          paymentPermission?.cc_gateway_name?.name === PaymentGateway.STRIPE) ||
        selectedMethod === "PayPal" ||
        selectedMethod === "Cash App Pay" ||
        selectedMethod === "Amazon Pay" ||
        (selectedMethod === "Bank" &&
          paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE)
      ) {
        dispatch(storePlanDetails({ selectedProducts: newProducts }));
      }
    }, [selectedMethod]);

    useEffect(() => {
      if (pMethodSelected.type !== "new_method") {
        setShowButtons(true);
      } else {
        if (
          selectedMethod === "Card" &&
          paymentPermission?.cc_gateway_name?.name !== PaymentGateway.STRIPE
        ) {
          setShowButtons(true);
        } else if (selectedMethod === "Wire") {
          setShowButtons(true);
        } else if (
          selectedMethod === "Bank" &&
          paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE
        ) {
          let check = checkBankManualFormOrPlaid({
            manual_ach: portalSettings?.manual_ach,
            validate_bank_account: portalSettings?.validate_bank_account,
            isManualForm,
          });
          if (!check) {
            setShowButtons(true);
          } else {
            setShowButtons(false);
          }
        } else {
          setShowButtons(false);
        }
      }
    }, [
      pMethodSelected,
      portalSettings,
      paymentPermission,
      selectedMethod,
      isManualForm,
    ]);

    const handleRefPayment = async () => {
      let moveAhead = await handleCheckDisclosureAndDuplicate({
        method: getMethod(),
      });
      if (!moveAhead) {
        setIsLoading(false);
        return;
      }
      try {
        if (pMethodSelected.type === "old_method") {
          await handleOldMethod();
        } else if (pMethodSelected.type === "saved_method") {
          await handleDefaultMethod();
        } else {
          switch (selectedMethod) {
            case "Card":
              if (
                paymentPermission?.cc_gateway_name?.name ===
                  PaymentGateway.STRIPE &&
                cardRef.current
              ) {
                console.log("8 =====", 8);
                await cardRef.current.callHandleClick();
              } else {
                await handleCreateNewCard();
              }
              break;
            case "Amazon Pay":
              console.log("9 =====", 9);
              if (amazonRef.current) {
                await amazonRef.current.callHandleClick();
              } else {
                await handleCreateNewCard();
              }
              break;
            case "Bank":
              let check = checkBankManualFormOrPlaid({
                manual_ach: portalSettings?.manual_ach,
                validate_bank_account: portalSettings?.validate_bank_account,
                isManualForm,
              });
              if (
                (paymentPermission?.ach_gateway_name?.name ===
                  PaymentGateway.STRIPE ||
                  check) &&
                bankRef.current
              ) {
                await bankRef.current.callHandleClick();
              } else {
                await handleCreateNewCard();
              }
              break;
            case "Cash App Pay":
              if (cashAppRef.current) {
                await cashAppRef.current.callHandleClick();
              }
              break;
            case "Wire":
              await handleCreateNewCard();
              break;
            case "PayPal":
              window.location.href = payPalOrder.url;
              break;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () =>
        isUpdateSubscriptionPaymentMethod
          ? handleUpdateMethod()
          : handleRefPayment(),
    }));

    let params = `?subscriptionId=${currentPlan?.id}&subsDrawer=true&amount=${amount}&lead_id=${currentPlan?.lead_id}&clusterid=${currentPlan?.clusterid}&subsId=${currentPlan?.id}`;

    const getSubscriptionName = () => {
      if (newProducts?.length > 0) {
        let title = newProducts?.[0]?.name || "";
        if (newProducts?.length > 1) {
          return (
            title?.charAt(0) +
            title?.slice(1) +
            ` and ${newProducts.length - 1} more`
          );
        } else {
          return title?.charAt(0) + title?.slice(1);
        }
      } else {
        return currentPlan?.plan_id > 0 ? "Payment Agreement" : "Custom Plan";
      }
    };

    const updatePaymentMethod = async (cpp_id: string) => {
      try {
        let payload = {
          subscription_id: currentPlan?.id,
          cpp_id: cpp_id || "",
        };

        let res: any = await dispatch(
          updateSubscriptionPaymentMethod(payload)
        ).unwrap();

        if (res?.body?.success) {
          await onPaymentMethodChanged();
          setPaymentSuccess(true);
          setCurrentStep(2);
          setScreen(true);
        } else {
          setPaymentSuccess(true);
          setCurrentStep(2);
          setScreen(false);
        }
      } catch (error) {
        setPaymentSuccess(true);
        setCurrentStep(2);
        setScreen(false);
        console.log("error ======", error);
      } finally {
        setShowPopup(false);
      }
    };

    const handleUpdateMethod = async () => {
      setIsBTNLoading(true);
      try {
        if (pMethodSelected.type === "saved_method") {
          await updatePaymentMethod(`${pMethodSelected?.id || ""}`);
        } else if (pMethodSelected.type === "new_method") {
          switch (selectedMethod) {
            case "Card":
              if (
                paymentPermission?.cc_gateway_name?.name ===
                  PaymentGateway.STRIPE &&
                cardRef.current
              ) {
                await cardRef.current.callHandleClick();
              } else {
                await handleCreateNewCard(true);
              }
              break;
            case "Amazon Pay":
              if (amazonRef.current) {
                await amazonRef.current.callHandleClick();
              } else {
                await handleCreateNewCard(true);
              }
              break;
            case "Bank":
              let check = checkBankManualFormOrPlaid({
                manual_ach: portalSettings?.manual_ach,
                validate_bank_account: portalSettings?.validate_bank_account,
                isManualForm,
              });
              if (
                (paymentPermission?.ach_gateway_name?.name ===
                  PaymentGateway.STRIPE ||
                  check) &&
                bankRef.current
              ) {
                await bankRef.current.callHandleClick();
              } else {
                await handleCreateNewCard(true);
              }
              break;
            case "Cash App Pay":
              if (cashAppRef.current) {
                await cashAppRef.current.callHandleClick();
              }
              break;
            case "Wire":
              await handleCreateNewCard(true);
              break;
            case "PayPal":
              // window.location.href = payPalOrder.url;
              break;
          }
        } else {
          await updatePaymentMethod(`${pMethodSelected?.id || ""}`);
        }
      } catch (error) {
        console.log("error ======", error);
      } finally {
        setIsBTNLoading(false);
      }
    };

    const verifyStripeCardPayment = async ({
      intent,
      error,
      orderId,
      email,
      principalAmount,
    }: VerifyStripeCardPaymentParams) => {
      if (error) {
        setPaymentSuccess(true);
        setScreen(false);
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
        await dispatch(onSetFinalDisclosureLoading(false));
        await dispatch(onSetShowDisclosure(false));
      } else {
        try {
          let payload = {
            lead_id: currentPlan?.lead_id,
            cluster_id: decode(id)?.split("_")?.[1],
            isrecurring:
              isUpdateSubscriptionPaymentMethod || isDowngradePlan ? "Y" : "N",
            x_payment_method: "CC",
            amount: principalAmount ?? amount ?? 0,
            accountid: userData?.data?.accountData?.accountid || "",
            first_name: userData?.data?.accountData?.first_name || "",
            last_name: userData?.data?.accountData?.last_name || "",
            phone_number: userData?.data?.accountData?.phone || "",
            module: "process_payment",
            transaction_id: intent || "",
            isScheduleOnetime: true,
            // schedule_id > 0 makes verify reset the subscription's next_billing_date
            // to today, which would apply the downgrade immediately instead of at
            // cycle end — send 0 so verify only tokenizes and saves the profile.
            schedule_id: isDowngradePlan ? 0 : currentPlan?.id,
            conv_fee: conFee,
            description: "Online Payment",
            payment_note: "Subscription",
            email: email || userData?.data?.accountData?.email || "",
            isPayForProduct: true,
            custom_conv: "on",
          };
          let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
          if (res?.body?.success) {
            if (!isUpdateSubscriptionPaymentMethod) {
              if (isDowngradePlan) {
                const isMethodUpdated = await applyDowngradeMethodChange(
                  res?.body?.data?.cppid
                );
                if (isMethodUpdated) {
                  await onUpdatePlan({
                    payment_method: "CC",
                    ...payload,
                    ...res?.body?.data?.paymentDetails,
                  });
                } else {
                  setPaymentSuccess(true);
                  setScreen(false);
                }
              } else {
                await onUpdatePlan({
                  payment_method: "CC",
                  ...payload,
                  order_id: res?.body?.data?.invoice_id,
                  ...res?.body?.data?.paymentDetails,
                });
              }
            } else {
              let cppid = res?.body?.data?.cppid;
              await updatePaymentMethod(cppid || "");
            }
          } else {
            setPaymentSuccess(true);
            setScreen(false);
          }
        } catch (err) {
          console.log("err ======", err);
          setPaymentSuccess(true);
          setScreen(false);
        } finally {
          dispatch(resetDuplicateOrder());
          dispatch(resetCheckDuplicatePayment());
          await dispatch(onSetFinalDisclosureLoading(false));
          await dispatch(onSetShowDisclosure(false));
        }
      }
    };

    const showPopup = useMemo(() => {
      if (isUpdateSubscriptionPaymentMethod) {
        if (
          currentPlan?.payment_collection_type === "MOF" ||
          currentPlan?.plan_id > 0 ||
          newProducts?.length <= 0
        ) {
          return false;
        }

        return true;
      }
      return false;
    }, [currentPlan]);

    const savedPaymentMethodList = useMemo(() => {
      if (!paymentMethodListData.length) return [];

      if (
        (currentPlan?.payment_collection_type === "MOF" ||
          !isUpdateSubscriptionPaymentMethod) &&
        currentPlan?.cpp_id
      ) {
        const cppId = Number(currentPlan?.cpp_id);

        const otherMethod = paymentMethodListData.filter(
          (a: any) => a.id !== cppId
        );

        const oldMethod = paymentMethodListData.find(
          (a: any) => a.id === cppId
        );

        if (oldMethod) {
          return [oldMethod, ...otherMethod];
        } else {
          return paymentMethodListData;
        }
      } else {
        return paymentMethodListData;
      }
    }, [currentPlan, paymentMethodListData]);

    useEffect(() => {
      if (savedPaymentMethodList.length > 0) {
        // Opening "Update payment method" must land on a selectable saved
        // method. The previous state pointed at the current method as an
        // "old_method", while the update flow intentionally renders no old
        // method for subscriptions. That left every visible radio unchecked
        // and made the action appear to open the wrong step.
        if (isUpdateSubscriptionPaymentMethod) {
          setPMethodSelected({
            id: Number(savedPaymentMethodList[0]?.id),
            type: "saved_method",
          });
        } else if (currentPlan?.cpp_id) {
          setPMethodSelected({
            id: Number(currentPlan?.cpp_id),
            type: "old_method",
          });
        } else {
          setPMethodSelected({
            id: Number(savedPaymentMethodList?.[0]?.id),
            type: "saved_method",
          });
        }
      } else {
        setPMethodSelected({ id: -1, type: "new_method" });
      }
    }, [
      savedPaymentMethodList,
      currentPlan,
      isUpdateSubscriptionPaymentMethod,
    ]);

    const handlePlaidNewBank = async (values: any) => {
      try {
        let payload = {
          x_card_num: values?.x_card_num?.replaceAll(" ", ""),
          x_exp_date: values?.x_exp_date,
          x_card_code: values?.x_card_code,
          choldername: values?.choldername,
          x_payment_method: "ACH",
          x_city: values?.x_city || userData?.data?.accountData?.city || "",
          x_address:
            values?.x_address || userData?.data?.accountData?.address1 || "",
          address2:
            values?.address2 || userData?.data?.accountData?.address2 || "",
          x_zip:
            values?.x_zip || userData?.data?.accountData?.postal_code || "",
          x_state: values?.x_state || userData?.data?.accountData?.state || "",
          x_country:
            values?.x_country || userData?.data?.accountData?.country || "",
          routing_number: values?.routing_number,
          account_number: values?.account_number,
          bank_name: bank,
          swift_code: values?.swift_code,
          card_funding_type: values?.card_funding_type,
          default: "Y",
          default_profile: "N",
          cluster_id: currentPlan?.clusterid,
          accountid: userData?.data?.accountData?.accountid,
          x_first_name: userData?.data?.accountData?.first_name,
          x_last_name: userData?.data?.accountData?.last_name,
          x_phone: userData?.data?.accountData?.phone || "",
          x_email: userData?.data?.accountData?.email,
          lead_id: currentPlan?.lead_id || "",
          gateway_id:
            selectedMethod === "Card"
              ? paymentPermission?.cc_gateway
              : paymentPermission?.ach_gateway || "",
          gateway_name:
            selectedMethod === "Card"
              ? paymentPermission?.cc_gateway_name?.name
              : paymentPermission?.ach_gateway_name?.name || "",
          account_holder_name: values?.choldername ?? "",
          card_type: iconType,
          accounts_id: userData?.data?.accountData?.id,
          pc_atid: values?.pc_atid,
        };
        let res: any = await dispatch(addCard(payload)).unwrap();
        if (res && res.body && res.body.success) {
          if (pMethodSelected.type === "new_method") {
            const { paymentMethod } = res.body.data;
            await updatePaymentMethod(paymentMethod?.id || "");
          } else {
            await handleUpdatePayment(res);
          }
        } else {
          setScreen(false);
          setPaymentSuccess(true);
        }
      } catch (error) {}
    };

    const handlePlaidBankPayment = async (
      values: any,
      isCCTokenAvailable: boolean
    ) => {
      try {
        if (isCCTokenAvailable) {
          if (pMethodSelected.type === "new_method") {
            return await updatePaymentMethod(values?.cppid || "");
          } else {
            const res = {
              body: {
                data: {
                  paymentMethod: {
                    id: values?.cppid || "",
                    cc_token: values?.cc_token || "",
                    cc_token2: values?.cc_token2 || "",
                  },
                },
              },
            };
            return await handleUpdatePayment(res);
          }
        } else {
          return await handlePlaidNewBank(values);
        }
      } catch (error) {
        console.log("error ======", error);
      } finally {
        dispatch(setIsPaymentProcessing(false));
      }
    };

    return (
      <div className="lg:pt-5 pt-[10px] w-full lg:gap-5 gap-3 flex flex-col lg:px-10 px-5">
        <div className="lg:text-2xl text-base text-txtPrimary flex flex-col lg:gap-2 gap-1 lg:pb-5 pb-[10px]">
          <p className="font-medium ">Select a payment method</p>
          <p className="lg:text-base text-sm text-txtParagraph">
            Select a payment method to use with your {getSubscriptionName()}.
          </p>
        </div>
        <div className="flex flex-col lg:gap-6 gap-5 max-w-[530px]">
          {savedPaymentMethodList?.length > 0 &&
            savedPaymentMethodList
              .slice(0, limits)
              .map((method: any) => (
                <MethodComponent
                  key={method.id}
                  method={method}
                  selectedMethod={pMethodSelected}
                  setSelectedMethod={setPMethodSelected}
                  currentPlanId={
                    (currentPlan?.payment_collection_type === "MOF" ||
                      !isUpdateSubscriptionPaymentMethod) &&
                    currentPlan?.cpp_id
                      ? currentPlan?.cpp_id
                      : -1
                  }
                />
              ))}
          {savedPaymentMethodList?.length > 3 && (
            <span
              className="cursor-pointer text-blue hover:!text-btnPrimaryHover underline"
              onClick={() => {
                if (savedPaymentMethodList?.length > limits) {
                  setLimits((state) => state + 3);
                } else {
                  setLimits(3);
                }
              }}
            >
              {savedPaymentMethodList?.length > limits
                ? "Show more"
                : "Show less"}
            </span>
          )}

          <div className="flex flex-col w-full">
            <div className="flex lg:gap-4 gap-2">
              <input
                className="customRadio focus-visible:outline-none cursor-pointer"
                type="radio"
                checked={pMethodSelected.id === -1}
                onChange={() => {
                  setPMethodSelected({ id: -1, type: "new_method" });
                }}
              />
              <span
                className="text-txtPrimary lg:text-base text-sm leading-[20.83px] cursor-pointer"
                onClick={() =>
                  setPMethodSelected({ id: -1, type: "new_method" })
                }
              >
                Add payment method
              </span>
            </div>
            <div
              className={`drawer ${
                pMethodSelected.id === -1 && "open"
              } flex flex-col w-full gap-5 `}
            >
              <PaymentMethodsComponent
                cardData={{
                  cardNumber,
                  isDateErr,
                  isErr,
                  cardErrMsg,
                  handleChangeCard,

                  validateCard: () =>
                    validateCard({
                      values,
                      iconType,
                      setIsErr,
                      setCardErrMsg,
                      setErrMsg,
                    }),
                  iconType,
                  setIconType,
                  setIsAddNewCardMakePayment,
                  isAddNewCardMakePayment,
                  showNewStripeCardForm: true,
                }}
                bankData={{
                  bankName,
                  numberNotmatchedError,
                  setnumberNotmatchedError,
                  handleChangeBank,
                  selectedState,
                  setSelectedState,
                  stripeBankURL: `${window.location.href}${params}&method=bank&isUpdateSubscriptionPaymentMethod=${isUpdateSubscriptionPaymentMethod}`,
                  isManualForm,
                  setIsManualForm,
                  handlePlaidBankPayment,
                }}
                paymentConfig={{
                  paymentMethodList,
                  selectedMethod,
                  setSelectedMethod,
                  amount,
                  isRecurring:
                    isUpdateSubscriptionPaymentMethod || isDowngradePlan,
                  isDowngradePlan: isDowngradePlan,
                  isPaymentLink: true,
                  isSubscribe: true,
                  isUpdateSubscription: true,
                  isUpdateSubscriptionPaymentMethod,
                }}
                paymentHandlers={{
                  createPayPalIntent,
                  verifyStripeCardPayment,
                }}
                paymentUrls={{
                  cashAppURL: `${window.location.href}${params}&method=cashapp&isUpdateSubscriptionPaymentMethod=${isUpdateSubscriptionPaymentMethod}`,
                  stripeAmazonPayURL: `${window.location.href}${params}&method=amazonPay&isUpdateSubscriptionPaymentMethod=${isUpdateSubscriptionPaymentMethod}`,
                }}
                formState={{
                  values,
                  email,
                  setEmail,
                  errMsg,
                  isRingLoading,
                }}
                paymentGatewayState={{
                  hasGooglePay: false,
                }}
                componentRefs={{
                  cashAppRef,
                  bankRef,
                  cardRef,
                  amazonRef,
                }}
                phoneAndAddressConfig={{}}
              />
            </div>
          </div>
        </div>
        <div className="w-full max-w-[530px] flex flex-col gap-5 pb-5">
          {showButtons && (
            <Button
              text="Update"
              className="w-full"
              isDisabled={
                pMethodSelected.type === "" ||
                (pMethodSelected.type === "new_method" &&
                  (isDisabled || numberNotmatchedError || isFeeLoading))
              }
              onClick={() => {
                if (showPopup) {
                  setShowPopup(true);
                } else {
                  handleMakePayment();
                }
              }}
              isLoading={isLoading || isFeeLoading}
            />
          )}

          {!isUpdateSubscriptionPaymentMethod && (
            <Button
              text="Back"
              variant="white"
              className="w-full"
              onClick={() => setCurrentStep((state: number) => state - 1)}
            />
          )}
        </div>
      </div>
    );
  }
);

const MethodComponent = ({
  method,
  selectedMethod,
  setSelectedMethod,
  currentPlanId,
}: any) => {
  return (
    <div key={method.id} className="flex w-full justify-between items-center">
      <div className="flex lg:gap-4 gap-2">
        <input
          className="customRadio focus-visible:outline-none cursor-pointer"
          type="radio"
          checked={selectedMethod.id === method.id}
          onChange={() => {
            if (currentPlanId === method.id) {
              setSelectedMethod({ id: method.id, type: "old_method" });
            } else {
              setSelectedMethod({ id: method.id, type: "saved_method" });
            }
          }}
        />
        <span
          className="cursor-pointer"
          onClick={() => {
            if (currentPlanId === method.id) {
              setSelectedMethod({ id: method.id, type: "old_method" });
            } else {
              setSelectedMethod({ id: method.id, type: "saved_method" });
            }
          }}
        >
          {getPaymentMethod({
            card_type: method?.cc_type,
            method: method?.payment_method,
            digits: method?.cc_4digit,
            account_number: method?.account_number,
            className: "!gap-[2px] !text-base",
            textClassName: "!text-txtPrimary",
            isHidden: true,
            bank_name: method?.bank_name || "",
            rowId: method.id
          })}
        </span>
      </div>
      {method?.cc_expiry && (
        <span className="text-sm text-txtPrimary">
          Expires {formatCardExpiry(method?.cc_expiry)}
        </span>
      )}
    </div>
  );
};

export default PaymentSubscribePlan;
